import { useEffect, useState } from 'react';
import { hasManualTradingOrigin } from '@/config/api';
import {
  mapPortfolioSnapshot,
  type PortfolioSnapshotData,
} from '@/services/manualTradingApi';
import { portfolioWsUrl } from '@/services/portfolioWsApi';

const RECONNECT_MS = 3000;

export function usePortfolioWs(enabled = true) {
  const [snapshot, setSnapshot] = useState<PortfolioSnapshotData | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !hasManualTradingOrigin()) {
      setConnected(false);
      return undefined;
    }

    let ws: WebSocket | null = null;
    let disposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (disposed) return;
      const url = portfolioWsUrl();
      if (!url) return;

      ws = new WebSocket(url);

      ws.onopen = () => {
        if (!disposed) {
          setConnected(true);
          setError(null);
        }
      };

      ws.onmessage = (evt) => {
        if (typeof evt.data !== 'string') return;
        try {
          const payload = JSON.parse(evt.data) as Record<string, unknown>;
          if (payload.type === 'connected') {
            setConnected(true);
            setError(null);
            return;
          }
          const next = mapPortfolioSnapshot(payload);
          if (next) {
            setSnapshot(next);
            setError(null);
          }
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
      };

      ws.onerror = () => {
        if (!disposed) {
          setConnected(false);
          setError('portfolio websocket error');
        }
      };

      ws.onclose = () => {
        if (!disposed) {
          setConnected(false);
          reconnectTimer = setTimeout(connect, RECONNECT_MS);
        }
      };
    };

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [enabled]);

  return { snapshot, connected, error };
}
